import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { EmptyState } from "@/components/Fields";
import { listPlanes, deletePlan, duplicarPlan } from "@/lib/prevelis";
import { formatFecha } from "@/lib/format";
import { toast } from "sonner";
import { Plus, FileText, ChevronRight, Trash2, Copy, X, Search } from "lucide-react";

const ESTADO_BADGE = {
  borrador: "bg-slate-100 text-slate-600 border-slate-200",
  en_revision: "bg-amber-50 text-amber-700 border-amber-200",
  generado: "bg-blue-50 text-blue-700 border-blue-200",
};
const ESTADO_LABEL = { borrador: "Borrador", en_revision: "En revisión", generado: "Generado" };
const iconBtn = "w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-600";
const FILTROS = [
  { value: "", label: "Todos" },
  { value: "borrador", label: "Borrador" },
  { value: "en_revision", label: "En revisión" },
  { value: "generado", label: "Generado" },
];

export default function Planes() {
  const [searchParams] = useSearchParams();
  const [planes, setPlanes] = useState([]);
  const [search, setSearch] = useState("");
  const [estado, setEstado] = useState(searchParams.get("estado") || "");
  const [dup, setDup] = useState(null); // plan a duplicar
  const [incFechas, setIncFechas] = useState(false);
  const [incFotos, setIncFotos] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    listPlanes({ search, estado }).then(setPlanes).catch(() => {});
  }, [search, estado]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const remove = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("¿Eliminar este plan? Esta acción no se puede deshacer.")) return;
    await deletePlan(id);
    toast.success("Plan eliminado");
    load();
  };

  const abrirDup = (e, p) => { e.stopPropagation(); setDup(p); setIncFechas(false); setIncFotos(false); };
  const confirmarDup = async () => {
    const incluir = [];
    if (incFechas) incluir.push("fechas");
    if (incFotos) incluir.push("fotos");
    const nuevo = await duplicarPlan(dup.id, incluir);
    setDup(null);
    toast.success("Plan duplicado");
    navigate(`/planes/${nuevo.id}`);
  };

  return (
    <Layout
      title="Planes de PRL"
      subtitle="Expedientes de prevención generados con PREVELIS"
      actions={
        <button
          onClick={() => navigate("/planes/nuevo")}
          data-testid="planes-nuevo"
          className="inline-flex items-center gap-2 h-11 px-4 rounded-lg bg-blue-700 text-white font-medium hover:bg-blue-800"
        >
          <Plus className="w-5 h-5" /> <span className="hidden sm:inline">Nuevo Plan de PRL</span>
        </button>
      }
    >
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="planes-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por título o empresa…"
            className="w-full h-11 pl-9 pr-3 rounded-lg border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
          />
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {FILTROS.map((f) => (
            <button
              key={f.value}
              data-testid={`planes-filtro-${f.value || "todos"}`}
              onClick={() => setEstado(f.value)}
              className={`h-11 px-4 rounded-lg text-sm font-medium border transition-colors ${
                estado === f.value ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {planes.length === 0 && !search && !estado ? (
        <div className="bg-white border border-slate-200 rounded-xl">
          <EmptyState
            icon={FileText}
            title="Sin planes de PRL"
            description="Crea tu primer Plan de PRL siguiendo el asistente paso a paso."
            action={
              <button onClick={() => navigate("/planes/nuevo")} data-testid="planes-empty-nuevo" className="inline-flex items-center gap-2 h-12 px-5 rounded-lg bg-blue-700 text-white font-medium hover:bg-blue-800">
                <Plus className="w-5 h-5" /> Nuevo Plan de PRL
              </button>
            }
          />
        </div>
      ) : planes.length === 0 ? (
        <div data-testid="planes-sin-resultados" className="bg-white border border-slate-200 rounded-xl py-16 text-center text-slate-500">
          No hay planes que coincidan con la búsqueda o el filtro.
        </div>
      ) : (
        <div className="space-y-3">
          {planes.map((p) => (
            <button
              key={p.id}
              data-testid={`plan-row-${p.id}`}
              onClick={() => navigate(`/planes/${p.id}`)}
              className="w-full text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-blue-300 transition-colors flex items-center gap-4"
            >
              <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-heading font-semibold text-slate-900 truncate">{p.titulo}</div>
                <div className="text-xs text-slate-500">{p.empresa_nombre} · Actualizado {formatFecha(p.updated_at)}</div>
              </div>
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${ESTADO_BADGE[p.estado] || ESTADO_BADGE.borrador}`}>
                {ESTADO_LABEL[p.estado] || p.estado}
              </span>
              <span onClick={(e) => abrirDup(e, p)} data-testid={`plan-dup-${p.id}`} className={iconBtn}>
                <Copy className="w-4 h-4" />
              </span>
              <span onClick={(e) => remove(e, p.id)} data-testid={`plan-delete-${p.id}`} className={iconBtn + " hover:bg-red-50 text-red-600"}>
                <Trash2 className="w-4 h-4" />
              </span>
              <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />
            </button>
          ))}
        </div>
      )}

      {dup && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDup(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="dup-dialog">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-heading font-semibold text-lg text-slate-900">Duplicar plan</h3>
              <button onClick={() => setDup(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Se copiarán empresa, centro, puestos, trabajadores y la visita (con su checklist).
              El análisis de IA, la revisión técnica y el documento <b>no</b> se copian: se generan de nuevo.
            </p>
            <label className="flex items-center gap-3 py-2 cursor-pointer">
              <input type="checkbox" data-testid="dup-fechas" checked={incFechas} onChange={(e) => setIncFechas(e.target.checked)} className="w-4 h-4 accent-blue-700" />
              <span className="text-sm text-slate-700">Incluir también las fechas de la visita</span>
            </label>
            <label className="flex items-center gap-3 py-2 cursor-pointer">
              <input type="checkbox" data-testid="dup-fotos" checked={incFotos} onChange={(e) => setIncFotos(e.target.checked)} className="w-4 h-4 accent-blue-700" />
              <span className="text-sm text-slate-700">Incluir también las fotografías</span>
            </label>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setDup(null)} className="h-11 px-4 rounded-lg border border-slate-300 font-medium hover:bg-slate-50">Cancelar</button>
              <button onClick={confirmarDup} data-testid="dup-confirmar" className="h-11 px-5 rounded-lg bg-blue-700 text-white font-medium hover:bg-blue-800">Duplicar</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
